// LandingPage: upload flow, saved scripts, and character selection (React version)
// TODO: Full conversion from vanilla TS — this is a working stub

import { useState, useRef, useCallback } from 'react';
import { useAppSelector, useAppDispatch, store } from '../store';
import { setParsedLines, setCharacters, setMyCharacters, addMyCharacter, removeMyCharacter,
         setScriptMeta, setEditedScriptLines, setRecordedAudio, setFlowSource } from '../store/scriptSlice';
import { setResumeIndex, clearRevealedCues } from '../store/practiceSlice';
import { getWorkingLines } from './OptionsMenu';
import { loadScripts, loadPersistedEdits, loadPersistedRecordings,
         loadSelectedCharacters, getScriptById, loadLastMode,
         applyRecordedAudioToLines, persistEditedLines } from '../lib/persistence';
import { parseScript, parseScriptPdf } from '../services/scriptParser';
import { isLikelyCharacterDisplayName } from '../lib/scriptParser';
import { stopSpeech } from '../services/speech';
import type { ViewName } from '../app';

interface LandingPageProps {
    onNavigate: (view: ViewName) => void;
    showCharacterSelect?: boolean;
}

export function LandingPage({ onNavigate, showCharacterSelect }: LandingPageProps) {
    const dispatch = useAppDispatch();
    const parsedLines = useAppSelector(s => s.script.parsedLines);
    const myCharacters = useAppSelector(s => s.script.myCharacters);
    const currentScriptName = useAppSelector(s => s.script.currentScriptName);

    const [errorMsg, setErrorMsg] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [uploadStatus, setUploadStatus] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    const savedScripts = loadScripts();

    // Character selection
    if (showCharacterSelect) {
        const workingLines = getWorkingLines();
        const lineCounts: Record<string, number> = {};
        const charSet = new Set<string>();
        workingLines.forEach(line => {
            if (line.character && isLikelyCharacterDisplayName(line.character)) {
                lineCounts[line.character] = (lineCounts[line.character] || 0) + 1;
                charSet.add(line.character);
            }
        });

        return (
            <div className="character-select-container" id="character-view">
                <h1 className="upload-title">Which character(s) are you?</h1>
                <p className="upload-subtitle">The app will read all unselected characters' lines</p>
                <div className="character-list">
                    {Array.from(charSet).map(char => (
                        <div
                            key={char}
                            className={`character-option ${myCharacters.includes(char) ? 'selected' : ''}`}
                            onClick={() => {
                                if (myCharacters.includes(char)) {
                                    dispatch(removeMyCharacter(char));
                                } else {
                                    dispatch(addMyCharacter(char));
                                }
                            }}
                        >
                            <div className="character-checkbox"></div>
                            <div>
                                <div className="character-name">{char}</div>
                                <div className="character-lines">{lineCounts[char]} lines</div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="btn-row">
                    <button className="btn btn-secondary" onClick={() => onNavigate('upload')}>Back</button>
                    <button
                        className="btn btn-primary btn-large"
                        disabled={myCharacters.length === 0}
                        onClick={() => {
                            if (myCharacters.length === 0) return;
                            // Save character selection
                            const saved = loadScripts();
                            const scriptIndex = saved.findIndex(s => s.name === currentScriptName);
                            if (scriptIndex !== -1) {
                                saved[scriptIndex].selectedCharacters = myCharacters;
                                localStorage.setItem('savedScripts', JSON.stringify(saved));
                            }
                            onNavigate('practice');
                        }}
                    >
                        Start Practice
                    </button>
                </div>
            </div>
        );
    }

    // Upload view
    async function handleFiles(files: File[]) {
        setErrorMsg('');
        setSuccessMsg('');
        const pdfFiles = files.filter(f => f.name.toLowerCase().endsWith('.pdf'));
        if (pdfFiles.length === 0) {
            setErrorMsg('Please choose a PDF file.');
            return;
        }

        for (const file of pdfFiles) {
            try {
                setIsUploading(true);
                const name = file.name.replace('.pdf', '');

                setUploadStatus('Reading PDF...');
                const parsed = await parseScriptPdf(file, name);

                if (!parsed.ok) {
                    setIsUploading(false);
                    setUploadStatus('');
                    setErrorMsg('Could not find playable dialogue in that PDF. Make sure it contains character names and dialogue.');
                    continue;
                }

                setUploadStatus('Parsing script lines...');
                autoSaveScript(name, parsed.scriptText);

                setUploadStatus('Finalizing...');
                dispatch(setScriptMeta({ name, id: null }));
                loadPersistedRecordings();
                loadPersistedEdits();
                dispatch(setMyCharacters([]));
                dispatch(setFlowSource('new'));

                setIsUploading(false);
                setUploadStatus('');
                setSuccessMsg(`Successfully loaded "${name}"!`);
                setTimeout(() => onNavigate('character'), 500);
            } catch (error: any) {
                setIsUploading(false);
                setUploadStatus('');
                console.error('PDF error:', file.name, error);

                // Show more detailed error message
                const errorMessage = error?.message || String(error);
                if (errorMessage.includes('401')) {
                    setErrorMsg('API authentication failed. Check your Anthropic API key.');
                } else if (errorMessage.includes('429')) {
                    setErrorMsg('Rate limit exceeded. Please wait a moment and try again.');
                } else if (errorMessage.includes('500') || errorMessage.includes('502') || errorMessage.includes('503')) {
                    setErrorMsg('Server error. Please try again in a moment.');
                } else if (errorMessage.includes('timeout')) {
                    setErrorMsg('Request timed out. Try a smaller PDF or try again.');
                } else {
                    setErrorMsg(`Could not parse "${file.name}". Error: ${errorMessage}`);
                }
            }
        }
    }

    function autoSaveScript(name: string, text: string) {
        const lines = text.split(/\n/);
        const charPattern = /^([A-Z][A-Z\s.''-]{1,25})(?:\s*\([^)]*\))?\s*$/;
        const tempChars = new Set<string>();
        let lineCount = 0;
        let currentChar: string | null = null;
        for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) continue;
            const match = trimmed.match(charPattern);
            if (match) {
                currentChar = match[1].trim();
                tempChars.add(currentChar);
            } else if (currentChar && !trimmed.startsWith('(')) {
                lineCount++;
            }
        }
        const saved = loadScripts();
        const existingIndex = saved.findIndex(s => s.name === name);
        const entry = { name, text, lineCount, characterCount: tempChars.size, date: new Date().toISOString() };
        if (existingIndex !== -1) {
            saved[existingIndex] = entry;
        } else {
            saved.unshift(entry);
        }
        localStorage.setItem('savedScripts', JSON.stringify(saved));
    }

    async function loadSavedScript(index: number) {
        const saved = loadScripts();
        if (!saved[index]?.text) { setErrorMsg('Script data not found'); return; }
        dispatch(setScriptMeta({ name: saved[index].name, id: saved[index].id || null }));
        dispatch(setRecordedAudio(saved[index].recordedAudio || null));
        dispatch(setEditedScriptLines(null));
        dispatch(setMyCharacters([]));
        dispatch(clearRevealedCues());
        dispatch(setFlowSource('saved'));

        if (!await parseScript(saved[index].text, saved[index].name)) {
            loadPersistedEdits();
            const edited = store.getState().script.editedScriptLines;
            if (edited && edited.length > 0) {
                dispatch(setParsedLines(edited));
            } else {
                setErrorMsg(`Could not parse "${saved[index].name}".`);
                return;
            }
        }
        loadPersistedRecordings();
        loadPersistedEdits();

        const lastMode = loadLastMode();
        const validChars = new Set(getWorkingLines().map(l => l.character).filter(Boolean));
        const restoredChars = loadSelectedCharacters(saved[index], validChars);
        if (restoredChars.size > 0) {
            dispatch(setMyCharacters(Array.from(restoredChars)));
            onNavigate('practice');
        } else {
            onNavigate('character');
        }
    }

    function deleteSavedScript(index: number) {
        const saved = JSON.parse(localStorage.getItem('savedScripts') || '[]');
        const name = saved[index]?.name || 'this script';
        if (confirm(`Delete "${name}"?`)) {
            saved.splice(index, 1);
            localStorage.setItem('savedScripts', JSON.stringify(saved));
        }
    }

    return (
        <div className="upload-container" id="upload-view">
            <h1 className="upload-title">Prepare for your scene</h1>

            <div className="upload-area">
                <div
                    className={`upload-box ${isUploading ? 'is-loading' : ''}`}
                    onClick={() => !isUploading && fileInputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); if (!isUploading) e.currentTarget.classList.add('dragover'); }}
                    onDragLeave={(e) => e.currentTarget.classList.remove('dragover')}
                    onDrop={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.remove('dragover');
                        if (isUploading) return;
                        const files = Array.from(e.dataTransfer.files).filter(f => f.name.toLowerCase().endsWith('.pdf'));
                        if (files.length > 0) handleFiles(files);
                        else setErrorMsg('Please choose a PDF file.');
                    }}
                >
                    <div className="upload-icon">{isUploading ? '⏳' : '📄'}</div>
                    <div className="upload-text">{isUploading ? uploadStatus : 'Upload script'}</div>
                    {!isUploading && <div className="upload-hint">Drop in a PDF or click to browse.</div>}
                </div>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf"
                    multiple
                    style={{ display: 'none' }}
                    onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        if (files.length > 0) handleFiles(files);
                        else setErrorMsg('Please choose a PDF file.');
                        e.target.value = '';
                    }}
                />
            </div>
            {errorMsg && <div className="message error">{errorMsg}</div>}
            {successMsg && <div className="message success">{successMsg}</div>}

            <div className="saved-scripts">
                <div className="saved-scripts-header">
                    <span className="saved-scripts-title">My Script Library</span>
                    <span className="saved-scripts-count">{savedScripts.length} script{savedScripts.length !== 1 ? 's' : ''}</span>
                </div>
                <div>
                    {savedScripts.length === 0 ? (
                        <div className="empty-library">No saved scripts yet.</div>
                    ) : (
                        savedScripts.map((script, index) => (
                            <div key={script.id || index} className="saved-script-item" onClick={() => loadSavedScript(index)}>
                                <div className="saved-script-info">
                                    <div className="saved-script-name">{script.name}</div>
                                    <div className="saved-script-meta">
                                        {script.lineCount || '?'} lines • {script.characterCount || '?'} characters • Saved {new Date(script.date).toLocaleDateString()}
                                    </div>
                                </div>
                                <span
                                    className="saved-script-delete"
                                    onClick={(e) => { e.stopPropagation(); deleteSavedScript(index); }}
                                >✕</span>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}
